import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown } from "tiptap-markdown";
import katex from "katex";
import {
  TextB, TextItalic, ListBullets, ListNumbers, Quotes, Link as LinkIcon, Image as ImageIcon, Table as TableIcon,
  MathOperations, ArrowCounterClockwise, ArrowClockwise, Rows, Columns, Trash, CaretDown, Code, X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import MarkdownEditor from "@/components/MarkdownEditor";
import { CALLOUTS } from "@/components/MarkdownContent";
import { CustomDocument, Callout, MathInline, MathBlock, mdToEditorMd } from "./extensions";
import { LESSON_TEMPLATES } from "./templates";

const QUICK_MATH = [
  { label: "Fraction", snippet: "\\frac{a}{b}" },
  { label: "Puissance", snippet: "x^{2}" },
  { label: "Indice", snippet: "x_{i}" },
  { label: "Racine", snippet: "\\sqrt{x}" },
  { label: "Somme", snippet: "\\sum_{i=1}^{n} x_i" },
  { label: "Pourcentage", snippet: "\\frac{a}{b} \\times 100" },
];

function TB({ onClick, active, disabled, title, children, testId, label }) {
  return (
    <Button type="button" size="sm" variant="ghost" onClick={onClick} disabled={disabled} title={title} aria-label={title} data-testid={testId}
      onMouseDown={(e) => e.preventDefault()}
      className={`h-8 min-w-8 px-2 ${active ? "bg-primary/15 text-primary" : ""}`}>
      {children}
      {label && <span className="ml-1 hidden text-xs sm:inline">{label}</span>}
    </Button>
  );
}

/**
 * Éditeur de contenu visuel (type traitement de texte). Le contenu reste stocké en Markdown :
 * mêmes props que l'ancien MarkdownEditor, qui reste disponible via « Mode avancé ».
 */
export default function RichEditor({ value, onChange, onUploadImage, placeholder, testIdPrefix = "rte", showTemplates = false }) {
  const [advanced, setAdvanced] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [linkBar, setLinkBar] = useState(null);       // { url }
  const [mathDlg, setMathDlg] = useState(null);       // { pos, latex, block, display }
  const lastMd = useRef(value || "");
  const fileRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ document: false, heading: { levels: [1, 2, 3, 4] } }),
      CustomDocument, Callout, MathInline, MathBlock,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { target: "_blank", rel: "noreferrer" } }),
      Image.configure({ inline: true }),
      Table.configure({ resizable: false }), TableRow, TableHeader, TableCell,
      Placeholder.configure({ placeholder: placeholder || "Écrivez le contenu de la séquence ici..." }),
      Markdown.configure({ html: true, tightLists: true, bulletListMarker: "-", linkify: false, breaks: false, transformPastedText: true, transformCopiedText: false }),
    ],
    content: mdToEditorMd(value || ""),
    onUpdate: ({ editor: ed }) => {
      const md = ed.storage.markdown.getMarkdown();
      lastMd.current = md;
      onChange(md);
    },
    editorProps: { attributes: { class: "rte-content prose prose-slate max-w-none min-h-[260px] px-4 py-3 focus:outline-none", "data-testid": `${testIdPrefix}-editor` } },
  });

  // Contenu modifié depuis l'extérieur (choix d'un modèle, retour du mode avancé...)
  useEffect(() => {
    if (!editor || advanced) return;
    if ((value || "") === lastMd.current) return;
    lastMd.current = value || "";
    editor.commands.setContent(mdToEditorMd(value || ""), false);
  }, [value, editor, advanced]);

  // Clic sur une formule -> boîte de modification
  useEffect(() => {
    if (!editor) return undefined;
    const dom = editor.view.dom;
    const h = (e) => setMathDlg({ ...e.detail });
    dom.addEventListener("rte-edit-math", h);
    return () => dom.removeEventListener("rte-edit-math", h);
  }, [editor, advanced]);

  if (advanced) {
    return (
      <div>
        <MarkdownEditor value={value} onChange={onChange} onUploadImage={onUploadImage} placeholder={placeholder} testIdPrefix={testIdPrefix} />
        <button type="button" onClick={() => setAdvanced(false)} className="mt-1.5 text-xs text-primary underline" data-testid={`${testIdPrefix}-visual`}>← Revenir à l'éditeur visuel</button>
      </div>
    );
  }
  if (!editor) return null;

  const isEmpty = !(value || "").trim();
  const inTable = editor.isActive("table");
  const inCallout = editor.isActive("callout");
  const headingValue = editor.isActive("heading", { level: 1 }) ? "1" : editor.isActive("heading", { level: 2 }) ? "2"
    : editor.isActive("heading", { level: 3 }) ? "3" : editor.isActive("heading", { level: 4 }) ? "4" : "p";

  const setBlockStyle = (v) => {
    const chain = editor.chain().focus();
    if (v === "p") chain.setParagraph().run();
    else chain.setHeading({ level: parseInt(v, 10) }).run();
  };

  const openLink = () => setLinkBar({ url: editor.getAttributes("link").href || "https://" });
  const applyLink = () => {
    const url = (linkBar.url || "").trim();
    if (!url || url === "https://") { editor.chain().focus().unsetLink().run(); setLinkBar(null); return; }
    if (editor.state.selection.empty && !editor.isActive("link")) {
      editor.chain().focus().insertContent({ type: "text", text: url, marks: [{ type: "link", attrs: { href: url } }] }).run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
    setLinkBar(null);
  };

  const onImageChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file || !onUploadImage) return;
    setUploading(true);
    try {
      const url = await onUploadImage(file);
      if (url) editor.chain().focus().setImage({ src: url, alt: file.name }).run();
    } finally { setUploading(false); }
  };

  const applyCallout = (c) => {
    if (inCallout) editor.chain().focus().updateAttributes("callout", { type: c.type, title: c.label }).run();
    else editor.chain().focus().setCallout({ type: c.type, title: c.label, level: 2 }).run();
  };

  const saveMath = () => {
    const latex = (mathDlg.latex || "").trim();
    if (!latex) { setMathDlg(null); return; }
    if (mathDlg.pos === undefined || mathDlg.pos === null) {
      editor.chain().focus().insertContent({ type: mathDlg.block ? "mathBlock" : "mathInline", attrs: mathDlg.block ? { latex } : { latex, display: false } }).run();
    } else {
      editor.chain().focus().command(({ tr }) => {
        const node = tr.doc.nodeAt(mathDlg.pos);
        if (node) tr.setNodeMarkup(mathDlg.pos, undefined, { ...node.attrs, latex });
        return true;
      }).run();
    }
    setMathDlg(null);
  };
  const deleteMath = () => {
    editor.chain().focus().command(({ tr }) => {
      const node = tr.doc.nodeAt(mathDlg.pos);
      if (node) tr.delete(mathDlg.pos, mathDlg.pos + node.nodeSize);
      return true;
    }).run();
    setMathDlg(null);
  };
  let mathPreview = "";
  if (mathDlg) {
    try { mathPreview = katex.renderToString(mathDlg.latex || "\\square", { throwOnError: false, displayMode: !!mathDlg.block }); } catch { mathPreview = ""; }
  }

  return (
    <div className="rounded-lg border border-border bg-card" data-testid={`${testIdPrefix}-rich`}>
      {showTemplates && isEmpty && (
        <div className="border-b border-border bg-secondary/30 p-3" data-testid={`${testIdPrefix}-templates`}>
          <p className="mb-2 text-sm font-semibold">Partir d'un modèle</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {LESSON_TEMPLATES.map((t) => (
              <button key={t.id} type="button" onClick={() => onChange(t.markdown)} data-testid={`${testIdPrefix}-tpl-${t.id}`}
                className="rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-primary hover:bg-primary/5">
                <span className="block text-sm font-semibold">{t.label}</span>
                <span className="block text-xs text-muted-foreground">{t.description}</span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Ou écrivez directement ci-dessous.</p>
        </div>
      )}

      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 rounded-t-lg border-b border-border bg-secondary/60 p-1.5 backdrop-blur" data-testid={`${testIdPrefix}-toolbar`}>
        <TB onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Annuler"><ArrowCounterClockwise size={16} /></TB>
        <TB onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rétablir"><ArrowClockwise size={16} /></TB>
        <span className="mx-1 h-5 w-px bg-border" />
        <select value={headingValue} onChange={(e) => setBlockStyle(e.target.value)} aria-label="Style du paragraphe" data-testid={`${testIdPrefix}-style`}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm">
          <option value="p">Texte normal</option>
          <option value="2">Titre</option>
          <option value="3">Sous-titre</option>
          <option value="4">Petit titre</option>
          {headingValue === "1" && <option value="1">Grand titre</option>}
        </select>
        <TB onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Gras" testId={`${testIdPrefix}-bold`}><TextB size={16} weight="bold" /></TB>
        <TB onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italique" testId={`${testIdPrefix}-italic`}><TextItalic size={16} /></TB>
        <span className="mx-1 h-5 w-px bg-border" />
        <TB onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Liste à puces" testId={`${testIdPrefix}-bullets`}><ListBullets size={16} /></TB>
        <TB onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Liste numérotée" testId={`${testIdPrefix}-numbers`}><ListNumbers size={16} /></TB>
        <TB onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Citation"><Quotes size={16} /></TB>
        <span className="mx-1 h-5 w-px bg-border" />
        <TB onClick={openLink} active={editor.isActive("link")} title="Lien" label="Lien" testId={`${testIdPrefix}-link`}><LinkIcon size={16} /></TB>
        <TB onClick={() => (onUploadImage ? fileRef.current?.click() : null)} disabled={!onUploadImage || uploading}
          title={onUploadImage ? "Insérer une image" : "Enregistrez d'abord la séquence pour pouvoir insérer des images"} label="Image" testId={`${testIdPrefix}-image-btn`}><ImageIcon size={16} /></TB>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImageChange} data-testid={`${testIdPrefix}-image-input`} />
        <TB onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insérer un tableau" label="Tableau" testId={`${testIdPrefix}-table`}><TableIcon size={16} /></TB>
        <TB onClick={() => setMathDlg({ pos: null, latex: "", block: false })} title="Insérer une formule mathématique" label="Formule" testId={`${testIdPrefix}-math`}><MathOperations size={16} /></TB>
        <span className="mx-1 h-5 w-px bg-border" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm" variant="ghost" className={`h-8 px-2 text-sm ${inCallout ? "bg-primary/15 text-primary" : ""}`} data-testid={`${testIdPrefix}-callout`} onMouseDown={(e) => e.preventDefault()}>
              Encadré <CaretDown size={12} className="ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" onCloseAutoFocus={(e) => { e.preventDefault(); editor.commands.focus(); }}>
            {CALLOUTS.map((c) => (
              <DropdownMenuItem key={c.type} onClick={() => applyCallout(c)} data-testid={`${testIdPrefix}-callout-${c.type}`}>
                <c.icon size={16} className="mr-2" /> {c.label}
              </DropdownMenuItem>
            ))}
            {inCallout && (<><DropdownMenuSeparator /><DropdownMenuItem onClick={() => editor.chain().focus().unsetCallout().run()} data-testid={`${testIdPrefix}-callout-remove`}><X size={16} className="mr-2" /> Retirer l'encadré</DropdownMenuItem></>)}
          </DropdownMenuContent>
        </DropdownMenu>
        <button type="button" onClick={() => setAdvanced(true)} className="ml-auto px-2 text-xs text-muted-foreground underline hover:text-foreground" data-testid={`${testIdPrefix}-advanced`}>
          <Code size={12} className="mr-1 inline" />Mode avancé
        </button>
      </div>

      {inTable && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-primary/5 p-1.5 text-xs" data-testid={`${testIdPrefix}-table-tools`}>
          <span className="px-2 font-semibold text-primary">Tableau :</span>
          <TB onClick={() => editor.chain().focus().addRowAfter().run()} title="Ajouter une ligne"><Rows size={15} className="mr-1" />+ ligne</TB>
          <TB onClick={() => editor.chain().focus().addColumnAfter().run()} title="Ajouter une colonne"><Columns size={15} className="mr-1" />+ colonne</TB>
          <TB onClick={() => editor.chain().focus().deleteRow().run()} title="Supprimer la ligne"><Trash size={14} className="mr-1" />ligne</TB>
          <TB onClick={() => editor.chain().focus().deleteColumn().run()} title="Supprimer la colonne"><Trash size={14} className="mr-1" />colonne</TB>
          <TB onClick={() => editor.chain().focus().deleteTable().run()} title="Supprimer le tableau"><Trash size={14} className="mr-1 text-accent" /><span className="text-accent">tableau</span></TB>
        </div>
      )}

      {linkBar && (
        <div className="flex items-center gap-2 border-b border-border bg-secondary/30 p-2" data-testid={`${testIdPrefix}-link-bar`}>
          <Input value={linkBar.url} onChange={(e) => setLinkBar({ url: e.target.value })} placeholder="https://..." className="h-8" autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(); } if (e.key === "Escape") setLinkBar(null); }} data-testid={`${testIdPrefix}-link-input`} />
          <Button type="button" size="sm" className="h-8 bg-primary hover:bg-primary/90" onClick={applyLink} data-testid={`${testIdPrefix}-link-ok`}>OK</Button>
          {editor.isActive("link") && <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => { editor.chain().focus().extendMarkRange("link").unsetLink().run(); setLinkBar(null); }}>Retirer</Button>}
          <button type="button" onClick={() => setLinkBar(null)} aria-label="Fermer" className="text-muted-foreground"><X size={16} /></button>
        </div>
      )}

      <EditorContent editor={editor} />
      {uploading && <p className="border-t border-border px-3 py-1 text-xs text-muted-foreground">Envoi de l'image...</p>}

      <Dialog open={!!mathDlg} onOpenChange={(o) => { if (!o) setMathDlg(null); }}>
        <DialogContent aria-describedby="rte-math-desc" data-testid={`${testIdPrefix}-math-dialog`}>
          <DialogHeader>
            <DialogTitle>{mathDlg && mathDlg.pos !== null && mathDlg.pos !== undefined ? "Modifier la formule" : "Insérer une formule"}</DialogTitle>
            <DialogDescription id="rte-math-desc">Écrivez la formule en notation LaTeX, ou utilisez les boutons pour partir d'un exemple.</DialogDescription>
          </DialogHeader>
          {mathDlg && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {QUICK_MATH.map((q) => (
                  <button key={q.label} type="button" onClick={() => setMathDlg({ ...mathDlg, latex: (mathDlg.latex ? mathDlg.latex + " " : "") + q.snippet })}
                    className="rounded-full border border-border px-2.5 py-1 text-xs hover:border-primary hover:text-primary">{q.label}</button>
                ))}
              </div>
              <div>
                <Label>Formule (LaTeX)</Label>
                <textarea value={mathDlg.latex} onChange={(e) => setMathDlg({ ...mathDlg, latex: e.target.value })} rows={2} autoFocus
                  className="mt-1 w-full rounded-md border border-input bg-background p-2 font-mono text-sm outline-none focus:ring-2 focus:ring-ring" data-testid={`${testIdPrefix}-math-input`} />
              </div>
              <div className="min-h-[56px] overflow-x-auto rounded-md bg-secondary/50 p-3 text-center" data-testid={`${testIdPrefix}-math-preview`} dangerouslySetInnerHTML={{ __html: mathPreview }} />
              {(mathDlg.pos === null || mathDlg.pos === undefined) && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={mathDlg.block} onChange={(e) => setMathDlg({ ...mathDlg, block: e.target.checked })} /> Afficher la formule sur sa propre ligne, centrée
                </label>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {mathDlg && mathDlg.pos !== null && mathDlg.pos !== undefined && <Button type="button" variant="outline" onClick={deleteMath}><Trash size={14} className="mr-1 text-accent" /> Supprimer</Button>}
            <Button type="button" onClick={saveMath} className="bg-primary hover:bg-primary/90" data-testid={`${testIdPrefix}-math-ok`}>{mathDlg && mathDlg.pos !== null && mathDlg.pos !== undefined ? "Enregistrer" : "Insérer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
